import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  CheckStockService,
  StockProduct,
  StockType,
} from './check-stock.service';
import { SelectOption } from '@bcl/design-system';
import { AccordionService } from 'src/app/services/accordion.service';

@Component({
  selector: 'app-check-stock',
  templateUrl: './check-stock.component.html',
  styleUrls: ['./check-stock.component.scss'],
})
export class CheckStockComponent implements OnInit, OnDestroy {
  @Input() visitId: number | null = null;
  @Input() showItemList = true;
  @Input() openByDefault = false;

  public loading = false;
  public products: StockProduct[] = [];
  public stockTypes: SelectOption<StockType>[] = [];
  public inStockOptions: SelectOption<string>[] = [
    { value: 'yes', label: 'CHECK_STOCK.FILTERS.IN_STOCK.YES' },
    { value: 'no', label: 'CHECK_STOCK.FILTERS.IN_STOCK.NO' },
    { value: 'all', label: 'CHECK_STOCK.FILTERS.IN_STOCK.ALL' },
  ];
  public isSelectorDisabled = false;
  public checkStockForm: FormGroup;

  private destroyed$ = new Subject<void>();

  constructor(
    private checkStockService: CheckStockService,
    private accordionService: AccordionService,
    private fb: FormBuilder
  ) {
    this.checkStockForm = this.fb.group({
      selectedType: ['my'],
      inStock: ['yes'],
    });
  }

  get selectedType(): StockType {
    return this.checkStockForm.get('selectedType')?.value;
  }

  get inStock(): string {
    return this.checkStockForm.get('inStock')?.value;
  }

  ngOnInit(): void {
    this.initializeStockTypes();
    this.setupFormSubscription();
    this.loadStock();

    // Open the accordion by default if specified
    if (this.openByDefault) {
      this.accordionService.openComponent('check-stock');
    }

    // Subscribe to accordion service to manage showItemList state
    this.accordionService
      .getCurrentOpenComponent()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((openComponentId) => {
        this.showItemList = openComponentId === 'check-stock';
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public showItems(): void {
    // Use accordion service to manage state
    if (this.showItemList) {
      this.accordionService.closeComponent('check-stock');
    } else {
      this.accordionService.openComponent('check-stock');
    }
  }

  private setupFormSubscription(): void {
    // Subscribe to both selectedType and inStock changes
    this.checkStockForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.loadStock();
      });
  }

  private initializeStockTypes(): void {
    this.stockTypes = [{ value: 'my', label: 'CHECK_STOCK.TYPES.MY' }];

    // Only add visit and compare options if visitId is provided
    if (this.visitId) {
      this.stockTypes.push(
        { value: 'visit', label: 'CHECK_STOCK.TYPES.VISIT' },
        { value: 'compare', label: 'CHECK_STOCK.TYPES.COMPARE' }
      );
      this.isSelectorDisabled = false;
    } else {
      // When visitId is null, disable the selector and only show "My Stock"
      this.checkStockForm.patchValue({ selectedType: 'my' });
      this.isSelectorDisabled = true;
    }
  }

  private loadStock(): void {
    const selectedType = this.checkStockForm.get('selectedType')?.value;
    this.loading = true;
    this.checkStockService
      .getStock(this.visitId, selectedType)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (products) => {
          // Filter out products with myAmount or amount less than 0
          let filteredProducts = products.filter(
            (product) =>
              (product.myAmount === undefined || product.myAmount >= 0) &&
              (product.amount === undefined || product.amount >= 0)
          );

          // Apply inStock filter
          if (this.inStock === 'yes') {
            filteredProducts = filteredProducts.filter((product) =>
              this.selectedType === 'compare'
                ? product.myAmount > 0
                : product.amount > 0
            );
          } else if (this.inStock === 'no') {
            filteredProducts = filteredProducts.filter((product) =>
              this.selectedType === 'compare'
                ? product.myAmount <= 0
                : product.amount <= 0
            );
          }
          // If inStock is 'all', no additional filtering needed

          this.products = filteredProducts;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading stock:', error);
          this.loading = false;
        },
      });
  }
}
